import { motion } from "framer-motion";
import { useState } from "react";
import { ArrowUpRight, Hammer, Home, Building2 } from "lucide-react";

const services = [
  {
    n: "01",
    label: "Handyman & Repairs",
    kicker: "The details.",
    body:
      "Drywall, trim, plumbing, doors, decks, punch-lists. The small work most crews skip — done the same day, done right.",
    image:
      "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxjcmFmdHNtYW4lMjB3b3JraW5nJTIwd29vZHxlbnwwfHx8fDE3ODM4MTI1NjV8MA&ixlib=rb-4.1.0&q=85",
    icon: Hammer,
  },
  {
    n: "02",
    label: "Residential Builds & Renovations",
    kicker: "The dream.",
    body:
      "Kitchens, baths, additions, whole-house remodels, ground-up new construction. From concept to keys — one crew, one contract, one standard.",
    image:
      "https://images.unsplash.com/photo-1691425700585-c108acad6467?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGhvbWUlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODM4MTI1NjV8MA&ixlib=rb-4.1.0&q=85",
    icon: Home,
  },
  {
    n: "03",
    label: "Commercial & Large-Scale",
    kicker: "The scale.",
    body:
      "Retail buildouts, office fit-outs, restaurants, warehouses, multi-unit residential. Bonded, insured, timeline-obsessed.",
    image:
      "https://images.unsplash.com/photo-1546414701-81cc6963c67f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxhcmNoaXRlY3R1cmFsJTIwY29uY3JldGUlMjBidWlsZGluZ3xlbnwwfHx8fDE3ODM4MTI1NjV8MA&ixlib=rb-4.1.0&q=85",
    icon: Building2,
  },
];

function ServiceCard({ item, i }) {
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const Icon = item.icon;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPos({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    });
  };

  return (
    <motion.article
      data-testid={`service-card-${item.n}`}
      onMouseMove={onMove}
      style={{
        "--mx": `${pos.x}%`,
        "--my": `${pos.y}%`,
      }}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{
        duration: 0.9,
        ease: [0.2, 0.8, 0.2, 1],
        delay: i * 0.12,
      }}
      className="spotlight-card group relative border border-white/10 bg-[#0a0a0a] p-6 md:p-8"
    >
      <div className="relative z-[2] flex items-start justify-between">
        <span className="font-mono text-xs tracking-widest text-white/50">
          {item.n} /
        </span>
        <Icon size={18} className="text-white/40" />
      </div>

      <div className="relative z-[2] mt-8 aspect-[4/3] overflow-hidden md:mt-10">
        <img
          src={item.image}
          alt={item.label}
          className="h-full w-full object-cover img-grayscale transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
      </div>

      <div className="relative z-[2] mt-6 md:mt-8">
        <div className="font-editorial text-lg italic text-[color:var(--accent)]">
          {item.kicker}
        </div>
        <h3 className="mt-2 font-oswald text-3xl uppercase leading-none tracking-tight text-white md:text-4xl">
          {item.label}
        </h3>
        <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/60 md:text-base">
          {item.body}
        </p>

        <a
          href="#contact"
          className="mt-6 inline-flex items-center gap-2 border-b border-white/20 pb-1 font-oswald text-xs uppercase tracking-[0.25em] text-white/80 transition hover:border-white hover:text-white"
        >
          Start this project
          <ArrowUpRight size={14} />
        </a>
      </div>
    </motion.article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-black py-24 md:py-36"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <header className="grid gap-8 md:grid-cols-[1fr_1.2fr] md:items-end">
          <div>
            <div className="eyebrow">— Chapter Two · Services</div>
            <h2 className="mt-6 font-oswald text-5xl uppercase leading-[0.9] tracking-tight text-white md:text-7xl">
              One phone.
              <br />
              <span className="text-white/40">All the trades.</span>
            </h2>
          </div>
          <p className="max-w-xl font-editorial text-lg italic leading-relaxed text-white/70 md:text-xl">
            We do all of it. Not &ldquo;we know a guy&rdquo; &mdash; we do it. Licensed,
            insured, and answering our own phone at 3 a.m.
          </p>
        </header>

        <div className="mt-14 grid gap-6 md:mt-20 md:grid-cols-3 md:gap-8">
          {services.map((s, i) => (
            <ServiceCard key={s.n} item={s} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
